import React from 'react'
import { Tooltip, Typography, Box } from '@material-ui/core'
import { motion } from 'framer-motion'
import {
  HPOTermContainer,
  TooltipHeader,
  HPOTermId,
  HPOCategoryBadge,
  HPOCategoryText,
  HPOObsoleteBadge,
  HPOObsoleteText,
  TooltipTitle,
  TooltipText,
  SynonymsSection,
  SynonymsLabel,
  SynonymChip,
  SynonymText,
  MoreChip,
  MoreText,
  AdditionalInfoSection,
  InfoNote,
  InfoDate,
  HPOFooter,
  HPOFooterText,
  ReplacementNotice,
  ReplacementText,
  ReviewChip,
  FormChipContainer,
} from '../styles/StyledComponents'

const HPOTermChips = ({ hpos, getHPOTermDetails, maxVisible = 4 }) => {
  if (!hpos?.hpos?.length) return null
  return (
    <FormChipContainer
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        maxWidth: '100%',
        justifyContent: 'flex-start',
        gap: 1.5,
      }}
    >
      {hpos.hpos.slice(0, maxVisible).map((term, index) => {
        const termDetails = getHPOTermDetails(term, index)
        return (
          <Box key={index} sx={{ flex: '1 1 0', minWidth: 0, maxWidth: 'calc(25% - 12px)', mb: 1 }}>
            <Tooltip
              title={
                <HPOTermContainer>
                  <TooltipHeader>
                    <Box>
                      <HPOTermId>{termDetails.id}</HPOTermId>
                      {termDetails.category && (
                        <HPOCategoryBadge>
                          <HPOCategoryText>{termDetails.category}</HPOCategoryText>
                        </HPOCategoryBadge>
                      )}
                    </Box>
                    {termDetails.is_obsolete && (
                      <HPOObsoleteBadge>
                        <HPOObsoleteText>OBSOLETE</HPOObsoleteText>
                      </HPOObsoleteBadge>
                    )}
                  </TooltipHeader>
                  <TooltipTitle>{termDetails.name}</TooltipTitle>
                  {termDetails.description && termDetails.description !== termDetails.name && (
                    <Box sx={{ mb: 2 }}>
                      <TooltipText isItalic={termDetails.description.includes('phenotypic abnormality')}>
                        {termDetails.description}
                      </TooltipText>
                    </Box>
                  )}
                  {termDetails.synonyms.length > 0 && (
                    <SynonymsSection>
                      <SynonymsLabel>Synonyms:</SynonymsLabel>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {termDetails.synonyms.slice(0, 5).map((synonym, synIndex) => (
                          <SynonymChip key={synIndex}>
                            <SynonymText>{synonym}</SynonymText>
                          </SynonymChip>
                        ))}
                        {termDetails.synonyms.length > 5 && (
                          <MoreChip>
                            <MoreText>+{termDetails.synonyms.length - 5} more</MoreText>
                          </MoreChip>
                        )}
                      </Box>
                    </SynonymsSection>
                  )}
                  {(termDetails.xrefs?.length > 0 || termDetails.comment || termDetails.creation_date) && (
                    <AdditionalInfoSection>
                      {termDetails.comment && termDetails.comment !== termDetails.description && (
                        <InfoNote>Note: {termDetails.comment}</InfoNote>
                      )}
                      {termDetails.creation_date && (
                        <InfoDate>Created: {new Date(termDetails.creation_date).toLocaleDateString()}</InfoDate>
                      )}
                    </AdditionalInfoSection>
                  )}
                  <HPOFooter>
                    <HPOFooterText>Human Phenotype Ontology (HPO) Term</HPOFooterText>
                  </HPOFooter>
                  {termDetails.is_obsolete && termDetails.replaced_by && (
                    <ReplacementNotice>
                      <ReplacementText>Replaced by: {termDetails.replaced_by}</ReplacementText>
                    </ReplacementNotice>
                  )}
                </HPOTermContainer>
              }
              placement="top"
              arrow
            >
              <motion.div whileHover={{ scale: 1.05 }} transition={{ type: 'spring', stiffness: 400, damping: 20 }}>
                <ReviewChip
                  label={
                    termDetails.id && termDetails.id !== 'Unknown' && termDetails.id.startsWith('HP:')
                      ? termDetails.id
                      : termDetails.name.length > 20
                      ? `${termDetails.name.substring(0, 20)}...`
                      : termDetails.name
                  }
                  chipColor="primary"
                  size="small"
                  sx={{
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily:
                      termDetails.id && termDetails.id !== 'Unknown' && termDetails.id.startsWith('HP:')
                        ? 'fontFamilyMono'
                        : 'inherit',
                    fontSize:
                      termDetails.id && termDetails.id !== 'Unknown' && termDetails.id.startsWith('HP:')
                        ? '0.75rem'
                        : '0.8rem',
                    fontWeight:
                      termDetails.id && termDetails.id !== 'Unknown' && termDetails.id.startsWith('HP:') ? 600 : 500,
                    maxWidth: '100%',
                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                    '&:hover': {
                      backgroundColor: 'action.selected',
                    },
                  }}
                />
              </motion.div>
            </Tooltip>
          </Box>
        )
      })}
      {hpos.hpos.length > maxVisible && (
        <Box sx={{ flex: '1 1 0', minWidth: 0, maxWidth: 'calc(25% - 12px)', mb: 1 }}>
          <Tooltip
            title={
              <Box sx={{ p: 1.5, maxWidth: '450px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'white', fontSize: '0.95rem' }}>
                  Additional HPO Terms ({hpos.hpos.length - maxVisible})
                </Typography>
                <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {hpos.hpos.slice(maxVisible).map((term, index) => {
                    const termDetails = getHPOTermDetails(term, index + maxVisible)
                    return (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          pb: 2,
                          borderBottom:
                            index < hpos.hpos.slice(maxVisible).length - 1
                              ? '1px solid rgba(255, 255, 255, 0.08)'
                              : 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'info.light',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              fontFamily: 'fontFamilyMono',
                              letterSpacing: '0.5px',
                            }}
                          >
                            {termDetails.id}
                          </Typography>
                          {termDetails.category && (
                            <Box
                              sx={{
                                px: 0.8,
                                py: 0.2,
                                backgroundColor: '#0288d115',
                                borderRadius: '6px',
                                border: '1px solid #0288d130',
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#03a9f4',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  textTransform: 'uppercase',
                                }}
                              >
                                {termDetails.category}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: 'white', mb: 1, fontSize: '0.9rem', lineHeight: 1.3 }}
                        >
                          {termDetails.name}
                        </Typography>
                        {termDetails.description && termDetails.description !== termDetails.name && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255, 255, 255, 0.75)',
                              fontSize: '0.75rem',
                              lineHeight: 1.4,
                              display: 'block',
                              mb: 1,
                              fontStyle: termDetails.description.includes('phenotypic abnormality')
                                ? 'italic'
                                : 'normal',
                            }}
                          >
                            {termDetails.description.length > 120
                              ? `${termDetails.description.substring(0, 120)}...`
                              : termDetails.description}
                          </Typography>
                        )}
                        {termDetails.synonyms.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.6)',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px',
                                }}
                              >
                                Synonyms:
                              </Typography>
                              {termDetails.synonyms.slice(0, 2).map((synonym, synIndex) => (
                                <Box
                                  key={synIndex}
                                  sx={{
                                    px: 0.8,
                                    py: 0.2,
                                    backgroundColor: '#4caf5020',
                                    borderRadius: '8px',
                                    border: '1px solid #4caf5035',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#4caf50',
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {synonym.length > 25 ? `${synonym.substring(0, 25)}...` : synonym}
                                  </Typography>
                                </Box>
                              ))}
                              {termDetails.synonyms.length > 2 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#4caf50',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    fontStyle: 'italic',
                                  }}
                                >
                                  +{termDetails.synonyms.length - 2} more
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                        {termDetails.is_obsolete && (
                          <Box sx={{ mt: 1 }}>
                            <Box
                              sx={{
                                px: 1,
                                py: 0.3,
                                backgroundColor: '#f4433620',
                                borderRadius: '6px',
                                border: '1px solid #f4433630',
                                display: 'inline-block',
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#f44336',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              >
                                OBSOLETE TERM
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            }
            placement="top"
            arrow
          >
            <ReviewChip
              label={`+${hpos.hpos.length - maxVisible} more`}
              chipColor="default"
              size="small"
              sx={{ width: '100%', cursor: 'pointer' }}
            />
          </Tooltip>
        </Box>
      )}
    </FormChipContainer>
  )
}

export default HPOTermChips
